import React, { useState, useEffect } from "react";
import { Dropdown, Nav, Navbar as BootstrapNavbar, Row, Col, Button } from "react-bootstrap";

// Custom wrapper component for the nested menu
const NestedNavMenu = ({ genres, manufacturers, handleNavigation, currentPath }) => {
  console.log('NestedNavMenu rendering with:', { genres: genres.length, manufacturers: manufacturers.length });

  return (
    <div className="nav-item-wrapper">
      <Dropdown>
        <Dropdown.Toggle
          variant="link"
          id="preset-packs-dropdown"
          className="nav-link btn btn-link p-0 border-0 bg-transparent text-decoration-none"
        >
          Serum Preset Packs
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item onClick={() => handleNavigation('/preset_packs')}>
            All Preset Packs
          </Dropdown.Item>

          <Dropdown.Divider />

          <Dropdown.Header>Browse by Genre</Dropdown.Header>
          {genres.map(genre => (
            <Dropdown.Item
              key={genre.id}
              onClick={() => handleNavigation(genre.link)}
            >
              {genre.name}
            </Dropdown.Item>
          ))}
          {genres.length === 0 && (
            <Dropdown.ItemText>Loading genres...</Dropdown.ItemText>
          )}

          <Dropdown.Divider />

          <Dropdown.Header>Browse by Manufacturer</Dropdown.Header>
          {manufacturers.map(manufacturer => (
            <Dropdown.Item
              key={manufacturer.id}
              onClick={() => handleNavigation(manufacturer.link)}
            >
              {manufacturer.name}
            </Dropdown.Item>
          ))}
          {manufacturers.length === 0 && (
            <Dropdown.ItemText>Loading manufacturers...</Dropdown.ItemText>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

/**
 * Navbar - Main navigation component for Xfer Records site
 *
 * This component handles the site navigation, user account dropdown,
 * and shopping cart functionality. It fetches data from the backend
 * and listens for cart update events to keep the cart display in sync.
 *
 * @param {Object} props Component props
 * @param {string} props.logoUrl URL to the site logo
 * @returns {React.ReactElement} The main navigation bar
 */
const Navbar = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [cartDropdownOpen, setCartDropdownOpen] = useState(false);
  const [navbarData, setNavbarData] = useState({
    user_signed_in: false,
    current_user: null,
    shopping_cart: { items: [], total: 0 },
    current_path: window.location.pathname,
    forums_enabled: false,
    can_view_preset_reports: false,
    genres: [],
    manufacturers: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [genres, setGenres] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);

  useEffect(() => {
    fetchNavbarData();

    // Set up event listener for cart updates
    const handleCartUpdate = () => {
      console.log("Navbar: Cart updated event received, refreshing navbar data");
      fetchNavbarData();
    };

    // Add event listener for cart updates
    window.addEventListener('cart-updated', handleCartUpdate);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate);
    };
  }, []);

  useEffect(() => {
    console.log('Navbar component mounted');

    fetch('/api/external/navbar')
      .then(response => response.json())
      .then(data => {
        console.log(`genres: ${data.genres ? data.genres.length : 0}, manufacturers: ${data.manufacturers ? data.manufacturers.length : 0}`);
        setGenres(data.genres || []);
        setManufacturers(data.manufacturers || []);
      })
      .catch(error => console.error('Error fetching navbar data:', error));
  }, []);

  const fetchNavbarData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/external/navbar');
      if (!response.ok) {
        throw new Error('Failed to fetch navbar data');
      }

      const data = await response.json();
      console.log('Navbar data loaded:', {
        genres: data.genres?.length || 0,
        manufacturers: data.manufacturers?.length || 0
      });
      setNavbarData(data);
      setGenres(data.genres || []);
      setManufacturers(data.manufacturers || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching navbar data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const toggleNavbar = () => {
    setIsOpen(!isOpen);
  };

  const removeFromCart = async (itemId) => {
    try {
      const response = await fetch(`/shopping_cart/remove.json?product_id=${itemId}`);
      if (response.ok) {
        // First fetch new data
        await fetchNavbarData();

        // Then dispatch the general cart update event
        const generalEvent = new CustomEvent('cart-updated');
        window.dispatchEvent(generalEvent);

        // Also dispatch a specific event with the removed item ID
        // This helps CartButton components know when to update their state
        const detailedEvent = new CustomEvent('cart-item-removed', {
          detail: { removedItemId: itemId }
        });
        window.dispatchEvent(detailedEvent);
      } else {
        console.error('Failed to remove item from cart');
      }
    } catch (err) {
      console.error('Error removing item from cart:', err);
    }
  };

  const handleNavigation = (url) => {
    console.log('Navigating to:', url);
    if (url) {
      window.location.href = url;
    }
  };

  // Debug logging for menu data
  console.log('Navbar render - genres:', genres.length, 'manufacturers:', manufacturers.length);

  // Show loading state while fetching data
  if (loading) {
    return (
      <BootstrapNavbar expand="lg" variant="dark" className="navbar-bg fixed-top">
        <div className="container">
          <a href="/" className="d-none d-sm-block xfer-logo">
            <img
              src={props.logoUrl}
              height="70"
              width="103"
              alt="Xfer Records"
            />
          </a>
        </div>
      </BootstrapNavbar>
    );
  }

  // Show error state if there was a problem
  if (error) {
    console.error('Navbar error:', error);
    // Fallback to just showing a simple navbar with home link
    return (
      <BootstrapNavbar expand="lg" variant="dark" className="navbar-bg fixed-top">
        <div className="container">
          <BootstrapNavbar.Brand href="/">Home</BootstrapNavbar.Brand>
        </div>
      </BootstrapNavbar>
    );
  }

  const {
    user_signed_in,
    current_user,
    shopping_cart,
    current_path,
    forums_enabled,
    can_view_preset_reports,
    genres: originalGenres,
    manufacturers: originalManufacturers
  } = navbarData;

  // Get cart items and total
  const cartItems = shopping_cart?.items || [];
  const cartTotal = shopping_cart?.total || 0;
  const formattedTotal = `$${cartTotal.toFixed(2)}`;

  return (
    <BootstrapNavbar expand="lg" variant="dark" className="navbar-bg fixed-top" onToggle={toggleNavbar} expanded={isOpen}>
      <div className="container">
        <BootstrapNavbar.Toggle aria-controls="xferNavbar" />

        <a href="/" className="d-none d-sm-block xfer-logo">
          <img
            src={props.logoUrl}
            height="70"
            width="103"
            alt="Xfer Records"
          />
        </a>

        <BootstrapNavbar.Collapse id="xferNavbar">
          <Nav className="me-auto">
            <Nav.Link
              href="/"
              active={current_path === "/"}
            >
              Home
            </Nav.Link>

            {forums_enabled ? (
              <Nav.Link
                href="/forums"
                active={current_path === "/forums"}
              >
                Forums
              </Nav.Link>
            ) : (
              <Nav.Link href="/feature_disabled/forums">
                Forums
              </Nav.Link>
            )}

            <Dropdown as={Nav.Item}
              className={current_path.includes("/products") ? "active" : ""}
            >
              <Dropdown.Toggle as={Nav.Link} id="products-dropdown">
                Products
              </Dropdown.Toggle>
              <Dropdown.Menu className="dropdown-menu-custom">
                <Dropdown.Item href="/products/serum-2">Serum 2</Dropdown.Item>
                <Dropdown.Item href="/products/nerve">Nerve</Dropdown.Item>
                <Dropdown.Item href="/products/cthulhu">Cthulhu</Dropdown.Item>
                <Dropdown.Item href="/products/lfo-tool">LFO Tool</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {can_view_preset_reports ? (
              <Dropdown as={Nav.Item}
                className={current_path === "/preset_packs" ? "active" : ""}
              >
                <Dropdown.Toggle as={Nav.Link} id="preset-admin-dropdown">
                  Preset Pack Admin
                </Dropdown.Toggle>
                <Dropdown.Menu className="dropdown-menu-custom">
                  <Dropdown.Item href="/preset_packs">Preset Packs</Dropdown.Item>
                  <Dropdown.Item href="/admin/quarterly_reports">Preset Sales Reports</Dropdown.Item>
                  <Dropdown.Item href="/admin/preset_packs/new">New Preset Submission</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <NestedNavMenu
                genres={genres}
                manufacturers={manufacturers}
                handleNavigation={handleNavigation}
                currentPath={current_path}
              />
            )}

            <Nav.Link href="https://support.xferrecords.com">
              FAQ & Support
            </Nav.Link>
          </Nav>

          <Nav className="ms-auto">
            {user_signed_in && current_user && (current_user.is_admin || current_user.is_support) && (
              <Nav.Link href="/admin">Admin</Nav.Link>
            )}

            {/* Shopping Cart Dropdown */}
            <Dropdown
              as={Nav.Item}
              className="cart-nav-dropdown"
              show={cartDropdownOpen}
              onToggle={(isOpen) => setCartDropdownOpen(isOpen)}
              align="end"
            >
              <Dropdown.Toggle
                as={Nav.Link}
                id="cart-dropdown"
                active={current_path.includes("/shopping_cart")}
                className="cart-toggle"
              >
                <span className="fa fa-shopping-cart"></span>
                <strong> Cart</strong>
                {cartTotal > 0 && (
                  <small> ({formattedTotal})</small>
                )}
              </Dropdown.Toggle>

              <Dropdown.Menu className="dropdown-menu-custom cart-dropdown-menu">
                {cartItems.length === 0 ? (
                  <Dropdown.Item className="text-center py-3">
                    Your cart is empty
                  </Dropdown.Item>
                ) : (
                  <>
                    {cartItems.map((item) => (
                      <Dropdown.Item key={item.id} className="cart-drop-item py-2">
                        <Row className="no-gutters">
                          <Col xs={3} className="cart-img-col pe-2">
                            <img
                              className="cart-item-img img-fluid"
                              src={item.image || props.logoUrl}
                              alt={item.name || "Product"}
                              onError={(e) => { e.target.src = props.logoUrl; }}
                            />
                          </Col>
                          <Col xs={6} className="cart-item-name align-self-center">
                            <span className="cart-item-name-title">{item.name || "Product"}</span>
                            <br />
                            <small>{item.short_description || ""}</small>
                          </Col>
                          <Col xs={3} className="align-self-center text-end">
                            <h5 className="mb-1">${typeof item.price === 'number' ? item.price.toFixed(2) : item.price}</h5>
                            <Button
                              variant="link"
                              size="sm"
                              className="remove-button p-0"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                removeFromCart(item.id);
                              }}
                            >
                              <i className="fa fa-minus"></i> Remove
                            </Button>
                          </Col>
                        </Row>
                      </Dropdown.Item>
                    ))}

                    <Dropdown.Divider />

                    <Dropdown.Item
                      href="/shopping_cart/checkout"
                      className="checkout-item py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="primary"
                        className="checkout-button btn-block w-100"
                      >
                        <i className="fa fa-shopping-cart me-2"></i>
                        Checkout {formattedTotal}
                      </Button>
                    </Dropdown.Item>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>

            {user_signed_in && current_user ? (
              <Dropdown as={Nav.Item}
                className={`userDropdown ${current_path.includes("/my_account") ? "active" : ""}`}
                align="end"
              >
                <Dropdown.Toggle as={Nav.Link} id="user-dropdown">
                  <span className="username">{current_user.username}</span>
                  <span className="avatar">
                    <img
                      src={`https://www.gravatar.com/avatar/${btoa(current_user.email)}?s=156&d=mm`}
                      height="45"
                      width="45"
                      alt={current_user.username}
                    />
                  </span>
                </Dropdown.Toggle>

                <Dropdown.Menu className="dropdown-menu-custom">
                  <Dropdown.Item href="/my_account">
                    <i className="fas fa-user"></i> <strong>Your Account</strong>
                    {current_user.is_impersonated && " :: Impersonated"}
                  </Dropdown.Item>

                  <Dropdown.Item href="/user_settings">
                    <i className="fas fa-cog"></i> User Settings
                  </Dropdown.Item>

                  {current_user.is_impersonated ? (
                    <Dropdown.Item
                      href="/user_impersonator/stop"
                      data-method="post"
                    >
                      <i className="fa fa-sign-out"></i> Back to admin
                    </Dropdown.Item>
                  ) : (
                    <Dropdown.Item
                      href="/users/sign_out"
                      data-method="delete"
                    >
                      <i className="fa fa-sign-out"></i> Sign out
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <>
                <Nav.Link href="/users/sign_in">Sign in</Nav.Link>
                <Nav.Link href="/users/sign_up">Sign up</Nav.Link>
              </>
            )}
          </Nav>
        </BootstrapNavbar.Collapse>
      </div>
    </BootstrapNavbar>
  );
};

export default Navbar;